import React from "react";
import styled, { css, useTheme } from "styled-components";
import { useHistory } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { LanguageNameSpaces, UsageLimitValue, UsageType } from "@shared/types";
import { isWhitelabelSubscriptionType } from "@shared/utils/misc";
import { getUsageTitleKey } from "@shared/features/settings/utils";
import { useQuotaTest } from "@shared/features/settings/hooks";
import usePlanUsage from "@shared/features/settings/subscription/hooks/usePlanUsage";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import Button from "common/Buttons/Button";
import Link from "common/Typography/Link";
import { InfinityIcon } from "common/Icons/Icons";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook";
import Panel from "common/NewLook/Panel.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import { BILLING_TWO_COLUMN_WIDTH } from "features/settings/Billing/layout.ts";
import {
  IS_WEB_VIEW,
  PURCHASE_API_SLOTS_SETTINGS_PATH,
} from "common/constants";
import PrimaryApiHelperTooltip from "features/settings/Billing/PrimaryApiHelperTooltip.tsx";
import UsageChart from "features/settings/Billing/UsageChart";
import {
  useHideBillingButtons,
  useUpgradePlanPath,
} from "features/settings/common/hooks.tsx";
import { PLAN_USAGE_CHART_HEIGHT } from "features/settings/common/constants";

interface UsageFigureProps {
  usageType: UsageType;
  currentUsage?: UsageLimitValue;
  usageLimit?: UsageLimitValue;
}

/** "3 / 10" in one line; red once the quota is used up. */
const UsageValue = ({
  usageType,
  currentUsage,
  usageLimit,
}: Pick<UsageFigureProps, "usageType" | "currentUsage" | "usageLimit">) => {
  const theme = useTheme();
  const { testQuota, getTotalQuota } = useQuotaTest();
  const totalQuota = getTotalQuota(usageType, usageLimit);
  const isOver = !!testQuota(usageType, currentUsage, usageLimit);
  return (
    <Figure>
      <Current $isOver={isOver}>{currentUsage || 0}</Current>
      <Limit>
        /{" "}
        {totalQuota === "inf" ? (
          <InfinityIcon
            fillColor={getNewLookTokens(theme).muted}
            style={{ fontSize: 14 }}
          />
        ) : (
          totalQuota
        )}
      </Limit>
    </Figure>
  );
};

/** The thin progress bar under a usage row (none for unlimited). */
const UsageBar = ({
  usageType,
  currentUsage,
  usageLimit,
}: Pick<UsageFigureProps, "usageType" | "currentUsage" | "usageLimit">) => {
  const { testQuota, getTotalQuota } = useQuotaTest();
  const totalQuota = getTotalQuota(usageType, usageLimit);
  // Unlimited: no bar, but keep its space so rows share one rhythm.
  if (typeof totalQuota !== "number") return <Bar $empty />;
  const isOver = !!testQuota(usageType, currentUsage, usageLimit);
  const current = typeof currentUsage === "number" ? currentUsage : 0;
  const percent =
    totalQuota > 0 ? Math.min(100, (current / totalQuota) * 100) : 0;
  return (
    <Bar>
      <BarFill $percent={percent} $isOver={isOver} />
    </Bar>
  );
};

/** The signal-trades card: open-trades figure, upgrade nudge and the chart. */
export const NewLookSignalTrades = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const history = useHistory();
  const upgradePath = useUpgradePlanPath();
  const hideUpgradeButton = useHideBillingButtons();
  const { user, isLoadingQuotas, activeTradeCurrentUsageValueProps } =
    usePlanUsage();

  // Pro and top-plan users have nothing to upgrade to.
  const showUpgrade = !user?.isPro() && !user?.isMaxSubscription();

  return (
    <Panel
      title={t("plan_usage.signal_trades")}
      actions={
        showUpgrade &&
        !hideUpgradeButton && (
          <UpgradeButton onClick={() => history.push(upgradePath)}>
            <Sparkles size={14} strokeWidth={NEW_LOOK_ICON_STROKE} />
            {t("plan_usage.upgrade")}
          </UpgradeButton>
        )
      }
    >
      {isLoadingQuotas ? (
        <div aria-busy={true}>
          <TradesSummary>
            <Skeleton width={160} height={16} />
          </TradesSummary>
          <Skeleton width="100%" height={PLAN_USAGE_CHART_HEIGHT} radius={8} />
        </div>
      ) : (
        <>
          <TradesSummary>
            <OpenTrades>
              <TileLabel>{t("plan_usage.currently_open")}</TileLabel>
              <UsageValue {...activeTradeCurrentUsageValueProps} />
            </OpenTrades>
            {showUpgrade && (
              <UpgradeText>{t("plan_usage.dont_miss_trades")}</UpgradeText>
            )}
          </TradesSummary>
          <ChartWrapper>
            <UsageChart />
          </ChartWrapper>
        </>
      )}
    </Panel>
  );
};

/**
 * Usage rows (label left, "3 / 30" right, thin bar under). A hairline list in
 * a side column, or a grid of the same rows when there is no room for one.
 */
export const NewLookFeatureUsage = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const history = useHistory();
  const {
    user,
    userUsageLimits,
    allQuotaUsage,
    isLoadingQuotas,
    currentUsageTypes,
  } = usePlanUsage();

  return (
    <FeaturesPanel title={t("plan_usage.features_current_usage")}>
      {isLoadingQuotas ? (
        <Rows aria-busy={true}>
          {(currentUsageTypes.length ? currentUsageTypes : [0, 1, 2, 3]).map(
            (usageType) => (
              <Row key={usageType}>
                <RowHead>
                  <Skeleton width={120} height={14} />
                  <Skeleton width={48} height={14} />
                </RowHead>
                <Skeleton width="100%" height={4} radius={2} />
              </Row>
            )
          )}
        </Rows>
      ) : (
        <Rows>
          {currentUsageTypes.map((usageType) => {
            const translationKey = getUsageTitleKey(
              usageType,
              user?.subscription_plan_type
            );
            const showPurchaseApiButton =
              usageType === UsageType.CLIENT &&
              isWhitelabelSubscriptionType(user?.subscription_plan_type) &&
              !user?.isExpired();
            const props = {
              usageType,
              currentUsage: allQuotaUsage?.[usageType],
              usageLimit: userUsageLimits?.[usageType],
            };
            return (
              <Row key={usageType}>
                <RowHead>
                  <RowLabel>
                    <TileLabel>
                      {translationKey && tCommon(translationKey)}
                    </TileLabel>
                    {showPurchaseApiButton && <PrimaryApiHelperTooltip />}
                  </RowLabel>
                  <RowValue>
                    {showPurchaseApiButton && !IS_WEB_VIEW && (
                      <PurchaseLink
                        onClick={() =>
                          history.push(PURCHASE_API_SLOTS_SETTINGS_PATH)
                        }
                      >
                        {t("plan_usage.purchase")}
                      </PurchaseLink>
                    )}
                    <UsageValue {...props} />
                  </RowValue>
                </RowHead>
                <UsageBar {...props} />
              </Row>
            );
          })}
        </Rows>
      )}
    </FeaturesPanel>
  );
};

/** Both cards in order (used where there is no side column). */
const NewLookPlanUsage = () => (
  <>
    <NewLookSignalTrades />
    <NewLookFeatureUsage />
  </>
);

export default NewLookPlanUsage;

/** Open-trades figure on the left, a muted nudge on the right. */
const TradesSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 24px;
  margin-bottom: 20px;
`;
const OpenTrades = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 10px;
`;
const UpgradeText = styled.div`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
/** Control sizing of the new look: 32 high, 12 padding, 6 icon gap, radius 8. */
const UpgradeButton = styled(Button)`
  &&.ant-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 12px;
    border-radius: ${NEW_LOOK_RADIUS.control}px;
  }
`;
const ChartWrapper = styled.div`
  min-width: 0;
  height: ${PLAN_USAGE_CHART_HEIGHT}px;
  overflow: hidden;
`;
const FeaturesPanel = styled(Panel)`
  @container billing (min-width: ${BILLING_TWO_COLUMN_WIDTH}px) {
    position: sticky;
    top: 16px;
  }
`;
const Rows = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 20px 32px;
  @container billing (min-width: ${BILLING_TWO_COLUMN_WIDTH}px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }
`;
const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  @container billing (min-width: ${BILLING_TWO_COLUMN_WIDTH}px) {
    padding: 14px 0;
    &:first-child {
      padding-top: 0;
    }
    &:last-child {
      padding-bottom: 0;
    }
    & + & {
      border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
    }
  }
`;
const RowHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 20px;
`;
const RowLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;
const RowValue = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex: none;
`;
const TileLabel = styled.div`
  min-width: 0;
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  @media (max-width: 359.98px) {
    white-space: normal;
  }
`;
const PurchaseLink = styled(Link)`
  && {
    ${newLookType.label};
    white-space: nowrap;
  }
`;
const Figure = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  flex: none;
`;
const Current = styled.span<{ $isOver: boolean }>`
  ${newLookType.body};
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: ${(p) =>
    p.$isOver ? getNewLookTokens(p.theme).down : getNewLookTokens(p.theme).ink};
`;
const Limit = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  font-variant-numeric: tabular-nums;
`;
const Bar = styled.div<{ $empty?: boolean }>`
  height: 4px;
  border-radius: 999px;
  background: ${(p) =>
    p.$empty ? "transparent" : getNewLookTokens(p.theme).hairline};
  overflow: hidden;
`;
const BarFill = styled.div<{ $percent: number; $isOver: boolean }>`
  height: 100%;
  width: ${(p) => p.$percent}%;
  border-radius: inherit;
  background: ${(p) => getNewLookTokens(p.theme).accent};
  ${(p) =>
    p.$isOver &&
    css`
      background: ${getNewLookTokens(p.theme).down};
    `}
`;
